/**
 * Image processing utilities for ANYW3AR webapp
 *
 * These utilities handle client-side image manipulation using the Canvas API.
 * Designed for browser compatibility including iOS Safari (stays under 5MP canvas limit).
 */

export const MAX_IMAGE_DIMENSION = 2000;
export const MIN_IMAGE_DIMENSION = 200;

export interface ResizeResult {
  file: File;
  wasResized: boolean;
  width: number;
  height: number;
}

/**
 * Resizes an image file if either dimension exceeds maxDimension.
 * Maintains aspect ratio by scaling the longest side to maxDimension.
 * Uses high-quality canvas downscaling for best results.
 *
 * @param file - The image File to potentially resize
 * @param maxDimension - Maximum allowed dimension (default: 2000)
 * @returns Promise resolving to ResizeResult with processed file and metadata
 * @throws Error if canvas context unavailable, image fails to load, or blob creation fails
 *
 * @example
 * // Resize a mobile photo (3264x2448) to fit within 2000x2000
 * const result = await resizeImageIfNeeded(mobilePhoto);
 * // result.file: resized File (2000x1500)
 * // result.wasResized: true
 * // result.width: 2000
 * // result.height: 1500
 */
export async function resizeImageIfNeeded(
  file: File,
  maxDimension: number = MAX_IMAGE_DIMENSION
): Promise<ResizeResult> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      // Clean up object URL immediately after load
      URL.revokeObjectURL(objectUrl);

      const { naturalWidth, naturalHeight } = image;

      // Check if resize is needed
      if (naturalWidth <= maxDimension && naturalHeight <= maxDimension) {
        resolve({
          file,
          wasResized: false,
          width: naturalWidth,
          height: naturalHeight,
        });
        return;
      }

      // Create canvas for resize operation
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Failed to get canvas context for resize"));
        return;
      }

      // Calculate new dimensions maintaining aspect ratio
      const aspectRatio = naturalWidth / naturalHeight;
      let newWidth: number;
      let newHeight: number;

      if (naturalWidth >= naturalHeight) {
        // Landscape or square - width is the longer/equal side
        newWidth = maxDimension;
        newHeight = Math.round(maxDimension / aspectRatio);
      } else {
        // Portrait - height is the longer side
        newHeight = maxDimension;
        newWidth = Math.round(maxDimension * aspectRatio);
      }

      // Set canvas size to new dimensions
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Enable high-quality downscaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Draw resized image
      ctx.drawImage(image, 0, 0, newWidth, newHeight);

      // Convert to blob preserving original MIME type
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to create blob from resized image"));
            return;
          }

          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });

          resolve({
            file: resizedFile,
            wasResized: true,
            width: newWidth,
            height: newHeight,
          });
        },
        file.type,
        0.92 // Quality for JPEG/WebP (ignored for PNG)
      );
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image for resize"));
    };

    image.src = objectUrl;
  });
}
